import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ZyToolNodeView from './zytool-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyTool: {
            insertZyTool: (options?: InsertZyToolOptions) => ReturnType;
        };
    }
}

export interface InsertZyToolOptions {
    name?: string;
    caption?: string;
    toolXMLToLoad?: string;
    height?: string | null;
    width?: string | null;
    options?: { [key: string]: any };
    alt_text?: string;
    instructions?: string;
    challenge?: boolean;
    parts?: number;
    optional?: boolean;
    image_ids?: string[];
    maImageNodeIds?: Record<string, string>;
}

export const ZyToolNodeHeadless = Node.create({
    name: 'zyTool',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            name: {
                default: '',
                parseHTML: element => element.getAttribute('data-tool-name') || '',
                renderHTML: attributes => ({
                    'data-tool-name': attributes.name,
                }),
            },
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-tool-id'),
                renderHTML: attributes => ({
                    'data-tool-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            toolXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-tool-xml') || '',
                renderHTML: attributes => ({
                    'data-tool-xml': attributes.toolXMLToLoad,
                }),
            },
            height: {
                default: null,
                parseHTML: element => element.getAttribute('data-height') || null,
                renderHTML: attributes => ({
                    'data-height': attributes.height,
                }),
            },
            width: {
                default: null,
                parseHTML: element => element.getAttribute('data-width') || null,
                renderHTML: attributes => ({
                    'data-width': attributes.width,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
            alt_text: {
                default: '',
                parseHTML: element => element.getAttribute('data-alt-text') || '',
                renderHTML: attributes => ({
                    'data-alt-text': attributes.alt_text,
                }),
            },
            instructions: {
                default: '',
                parseHTML: element => element.getAttribute('data-instructions') || '',
                renderHTML: attributes => ({
                    'data-instructions': attributes.instructions,
                }),
            },
            challenge: {
                default: false,
                parseHTML: element => element.getAttribute('data-challenge') === 'true',
                renderHTML: attributes => ({
                    'data-challenge': attributes.challenge ? 'true' : 'false',
                }),
            },
            parts: {
                default: 1,
                parseHTML: element => parseInt(element.getAttribute('data-parts') || '1', 10),
                renderHTML: attributes => ({
                    'data-parts': attributes.parts,
                }),
            },
            optional: {
                default: false,
                parseHTML: element => element.getAttribute('data-optional') === 'true',
                renderHTML: attributes => ({
                    'data-optional': attributes.optional ? 'true' : 'false',
                }),
            },
            image_ids: {
                default: [],
                parseHTML: element => {
                    const ids = element.getAttribute('data-image-ids');
                    return ids && ids.trim()
                        ? ids
                              .split(',')
                              .map(id => id.trim())
                              .filter(id => id)
                        : [];
                },
                renderHTML: attributes => ({
                    'data-image-ids': attributes.image_ids?.join(',') || '',
                }),
            },
            maImageNodeIds: {
                default: {},
                parseHTML: element => {
                    try {
                        return JSON.parse(element.getAttribute('data-ma-image-node-ids') || '{}');
                    } catch {
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-ma-image-node-ids': JSON.stringify(attributes.maImageNodeIds || {}),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="zy-tool"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'zy-tool',
                class: 'zy-tool-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertZyTool:
                (options: InsertZyToolOptions = {}) =>
                ({ commands }) => {
                    const defaultToolXML = `<zyTool name="${options.name || ''}" caption="${options.caption || ''}">
<zyInstructions></zyInstructions>
<zyOptions>
</zyOptions>
<zyAltDescription>
</zyAltDescription>
</zyTool>`;

                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            name: options.name || '',
                            caption: options.caption || '',
                            toolXMLToLoad: options.toolXMLToLoad || defaultToolXML,
                            height: options.height || null,
                            width: options.width || null,
                            options: options.options || {},
                            alt_text: options.alt_text || '',
                            instructions: options.instructions || '',
                            challenge: options.challenge || false,
                            parts: options.parts || 1,
                            optional: options.optional || false,
                            image_ids: options.image_ids || [],
                            maImageNodeIds: options.maImageNodeIds || {},
                        },
                    });
                },
        };
    },
});

export const ZyToolNode = ZyToolNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyToolNodeView);
    },
});

export default ZyToolNode;
